import React from "react";
import { useI18n } from "../../i18n/I18nContext";
import { C } from "../../constants/design";
import { LeagueMember } from "../../types/game";

interface LeagueStandingsProps {
  wins: number;
  draws: number;
  losses: number;
  played: number;
  points: number;
  winrate: number;
  totalTeamPower: number;
  goalkeeperLevel: number;
  leagueMembers: LeagueMember[];
  realPlayersCount: number;
  botCount: number;
  loading?: boolean;
}

const TEAM_NAME = "Hyundai FC";

export function LeagueStandings({
  wins,
  draws,
  losses,
  played,
  points,
  winrate,
  totalTeamPower,
  goalkeeperLevel,
  leagueMembers,
  realPlayersCount,
  botCount,
  loading,
}: LeagueStandingsProps) {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ── Team header ── */}
      <div
        style={{
          background: "linear-gradient(160deg,#0e1a2e,#0a1020)",
          border: `1.5px solid rgba(0,211,127,0.18)`,
          borderRadius: 20,
          padding: "18px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "rgba(0,211,127,0.6)",
                letterSpacing: 2,
                textTransform: "uppercase",
                marginBottom: 3,
              }}
            >
              {t("leagueOverview.communityLeagueLabel")}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>
              {TEAM_NAME}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.35)",
                marginBottom: 2,
              }}
            >
              {t("leagueOverview.teamComposition", realPlayersCount, botCount)}
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.turf }}>
              ⚡ {totalTeamPower} PWR
            </div>
          </div>
        </div>

        {/* Goalkeeper level */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: played > 0 ? 14 : 0,
            background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.2)",
            borderRadius: 10,
            padding: "8px 12px",
          }}
        >
          <span style={{ fontSize: 20 }}>🧤</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>
              {t("leagueOverview.botGkLevel", goalkeeperLevel)}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
              {t("leagueOverview.gkUpgradeHint")}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
            }}
          >
            {Array.from({ length: Math.min(goalkeeperLevel, 5) }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: "#3B82F6",
                  opacity: 0.8 + i * 0.04,
                }}
              />
            ))}
          </div>
        </div>

        {/* Record */}
        {played > 0 && (
          <div
            style={{
              background: "rgba(0,0,0,0.25)",
              borderRadius: 14,
              padding: "12px 14px",
            }}
          >
            {/* Tableau classement style football */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr repeat(5,auto)",
                gap: "6px 12px",
                marginBottom: 10,
                fontSize: 11,
                fontWeight: 800,
                color: "rgba(255,255,255,0.3)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              <span>Team</span>
              <span style={{ textAlign: "center" }}>P</span>
              <span style={{ textAlign: "center" }}>W</span>
              <span style={{ textAlign: "center" }}>D</span>
              <span style={{ textAlign: "center" }}>L</span>
              <span style={{ textAlign: "center" }}>Pts</span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr repeat(5,auto)",
                gap: "6px 12px",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span style={{ color: C.turf, fontWeight: 900 }}>{TEAM_NAME}</span>
              <span style={{ textAlign: "center", color: C.muted }}>{played}</span>
              <span style={{ textAlign: "center", color: C.turf }}>{wins}</span>
              <span style={{ textAlign: "center", color: C.gold }}>{draws}</span>
              <span style={{ textAlign: "center", color: C.live }}>{losses}</span>
              <span
                style={{
                  textAlign: "center",
                  fontWeight: 900,
                  color: "#fff",
                }}
              >
                {points}
              </span>
            </div>

            {/* Winrate bar */}
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
                  {t("leagueOverview.matchesPlayedCount", played)}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: C.turf,
                  }}
                >
                  {t("leagueOverview.winsPercent", winrate)}
                </span>
              </div>
              <div
                style={{
                  height: 5,
                  background: "rgba(255,255,255,0.06)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 999,
                    background: `linear-gradient(90deg,${C.turf},rgba(45,227,107,0.5))`,
                    width: `${winrate}%`,
                    transition: "width 0.8s ease",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Roster ── */}
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "rgba(255,255,255,0.35)",
            letterSpacing: 2,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {t("leagueOverview.rosterLabel")}
        </div>
        <div
          style={{
            background: C.surface,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.06)",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: C.muted,
                fontSize: 13,
              }}
            >
              {t("common.loading")}
            </div>
          ) : (
            leagueMembers.map((member, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "11px 14px",
                  background: member.isMe ? "rgba(45,227,107,0.05)" : "transparent",
                  borderBottom:
                    idx !== leagueMembers.length - 1
                      ? "1px solid rgba(255,255,255,0.04)"
                      : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      color:
                        idx === 0
                          ? C.gold
                          : "rgba(255,255,255,0.25)",
                      width: 18,
                      textAlign: "center",
                    }}
                  >
                    #{idx + 1}
                  </span>
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: member.isBot
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(45,227,107,0.08)",
                      border: `1px solid ${
                        member.isMe
                          ? "rgba(45,227,107,0.4)"
                          : "rgba(255,255,255,0.08)"
                      }`,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {member.isBot ? "🤖" : "⚽"}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: member.isMe ? 800 : 600,
                      color: member.isBot
                        ? "rgba(255,255,255,0.3)"
                        : member.isMe
                        ? C.turf
                        : C.text,
                    }}
                  >
                    {member.name}
                    {member.isMe && (
                      <span
                        style={{
                          fontSize: 10,
                          marginLeft: 6,
                          background: "rgba(45,227,107,0.15)",
                          color: C.turf,
                          borderRadius: 5,
                          padding: "1px 5px",
                          fontWeight: 700,
                        }}
                      >
                        {t("leagueOverview.meBadgeInline")}
                      </span>
                    )}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: member.isBot
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(255,255,255,0.5)",
                  }}
                >
                  ⚡ {member.power}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
